export const DietMenuCalendar = (data: IDietMenuCalendar): IDietMenuCalendarModel =>
	Object.keys(data).reduce((acc, key) => {
		// @ts-ignore - Type 'string' is not assignable to type 'Date'.
		acc[new Date(key)] = data[key]
		return acc
	}, {})

export const DietMenuVariantMeal = (json: IDietMenuVariantMeal): IDietMenuVariantMealModel => {
	const {
		name,
		short_name,
		internal_name,
		is_active,
		max_order_qty,
		min_order_qty,
		meal_kind,
		meal_type,
		meal_price_tier,
		serve_cold,
		serve_warm,
		description,
		preparation_difficulty,
		receipe,
		updated_at,
		created_at,
		allergens,
		variants,
		dietary_tags,
		media,
		...rest
	} = json

	return {
		...rest,
		...DietAveragePricesToModel(rest),
		name: LangContentToModel(name),
		shortName: LangContentToModel(short_name),
		internalName: internal_name,
		allergens: allergens ? allergens.map(a => DietAllergen(a)) : [],
		isActive: is_active,
		maxOrderQty: max_order_qty,
		minOrderQty: min_order_qty,
		mealKind: meal_kind,
		mealType: meal_type,
		mealPriceTier: meal_price_tier,
		isServeCold: serve_cold,
		isServeWarm: serve_warm,
		variants: variants ? variants.map(DietMealVariant) : [],
		description: description ? LangContentToModel(description) : null,
		preparationDifficulty: preparation_difficulty,
		receipe: receipe ? LangContentToModel(receipe) : null,
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
		media: media ? media.map(Media) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMenuVariantPivot = (json: IDietMenuVariantPivot): IDietMenuVariantPivotModel => {
	const { kcal, meal_type, meal_kind, ...rest } = json

	return {
		...rest,
		kcal: Number(kcal),
		mealType: meal_type,
		mealKind: meal_kind,
	}
}

export const DietMenuVariant = (json: IDietMenuVariant): IDietMenuVariantModel => {
	const {
		meal_id,
		is_active,
		meal_size_id,
		preparation_time,
		dietary_tags,
		updated_at,
		created_at,
		pivot,
		meal,
		components,
		nutritional_parameters,
		...rest
	} = json

	return {
		...rest,
		...DietVariantPricesToModel(rest),
		mealId: meal_id,
		isActive: is_active,
		mealSizeId: meal_size_id,
		preparationTime: preparation_time,
		meal: DietMenuVariantMeal(meal),
		pivot: DietMenuVariantPivot(pivot),
		dietaryTags: dietary_tags ? dietary_tags.map(d => DietDietaryTag(d)) : [],
		nutritionalParameters: nutritional_parameters
			? nutritional_parameters.map(NutritionalParameterWithValue)
			: null,
		components: components ? components.map(DietMealComponent) : [],
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMenu = (json: IDietMenu): IDietMenuModel => {
	const { date, meals, variants, dietary_diet_id, published_at, ...rest } = json
	return {
		...rest,
		date: new Date(date),
		meals: meals ? meals.map(DietMeal) : [],
		variants: variants ? variants.map(DietMenuVariant) : [],
		dietaryDietId: dietary_diet_id,
		publishedAt: published_at ? new Date(published_at) : undefined,
	}
}

/**
 * Payloads
 */
export const DietMenuToPayload = (options: IDietMenuPayloadMapperOptions): IDietMenuPayload => {
	const { status, date, dietID, isMenuSelected, meals, matrices, variants } = options

	let mealsMapper: IDietMenuPayloadMeal[] = []
	let variantsMapper: IDietMenuVariantPayload[] = []

	if (isMenuSelected) {
		mealsMapper = meals.map(m => ({ id: m.id, meal_kind: m.mealKind, meal_type: m.mealType }))
	} else {
		variantsMapper = variants
			.filter(v => {
				if (v.pivot.mealType === EMealType.POST_WORKOUT) return true

				return matrices.some(m => m.mealType === v.pivot.mealType && m.kcal === v.pivot.kcal)
			})
			.map(v => ({
				id: v.id,
				kcal: String(v.pivot.kcal),
				meal_kind: v.pivot.mealKind,
				meal_type: v.pivot.mealType,
			}))
	}

	return {
		status,
		date: dateToFilterFormat(date),
		dietary_diet_id: dietID,
		meals: mealsMapper,
		variants: variantsMapper,
	}
}

export const dietMenuVariantPayloadToJSON = (payload: IDietMenuPayloadVariantModel): IDietMenuPayloadVariant => {
	const { mealKind, mealType, ...rest } = payload

	return {
		...rest,
		meal_kind: mealKind,
		meal_type: mealType,
	}
}

export const dietMenuPayloadToJSON = (payload: IDietMenuPayloadModel): IDietMenuPayload => {
	const { dietaryDietId, variants, date, ...rest } = payload

	return {
		...rest,
		date: dateToFilterFormat(date),
		dietary_diet_id: dietaryDietId,
		variants: variants ? variants.map(v => dietMenuVariantPayloadToJSON(v)) : [],
	}
}

export const DietMealToVariant = (payload: IDietMealModel, kcal: number): IDietMenuVariantModel => {
	const { mealType, mealKind, ...rest } = payload

	return {
		...rest,
		pivot: {
			kcal,
			mealType: payload.mealType,
			mealKind: payload.mealKind,
		},
	}
}

export const DietVariantToPayload = (
	variant: IDietMenuVariantModel,
	variants: IDietMealVariantModel[],
): IDietMenuPayloadVariantModel => {
	const selectedVariant = variants.find(v => v.mealId === variant.mealId && v.mealSizeId === variant.mealSizeId)

	if (!selectedVariant)
		throw new Error(
			'Podczas mapowania wariantu, wystąpił błąd - nie znaleziono wybranego wariantu, jeśli błąd się będzie powtarzał, skontaktuj się z administratorem',
		)

	const { pivot } = variant

	return {
		id: selectedVariant.id,
		kcal: String(pivot.kcal),
		mealKind: pivot.mealKind,
		mealType: pivot.mealType,
	}
}

/**
 * Duplicate - Payloads
 */
export const DietMenuDuplicateToPayload = (
	event: IFormSubmitEvent<IDietMenuDuplicateForm>,
	model: IDietMenuDuplicateFormOther,
): IDietMenuDuplicatePayload => {
	const { isAutoPublish } = event.values
	const { dateFrom, datesTo, dietFromId, dietToIds, mealTypes, meals, variants, isSynchronizeDates } = model

	return {
		meals,
		variants,
		date_from: Array.isArray(dateFrom)
			? dateFrom.map(date => dateToFilterFormat(date))
			: dateToFilterFormat(dateFrom),
		diet_from: dietFromId,
		date_to: Array.isArray(datesTo) ? datesTo.map(date => dateToFilterFormat(date)) : dateToFilterFormat(datesTo),
		diet_to_ids: dietToIds,
		meal_types: mealTypes,
		is_auto_publish: isAutoPublish,
		is_synchronize_dates: isSynchronizeDates,
	}
}
